import { createRoot } from "react-dom/client";
import { PRERENDER_ATTR, hasStoredAuthSession } from "./lib/prerenderHydration";
import "./index.css";
import { applyFeatureFlagOverridesFromURL } from "./lib/features";
import { installOverlayPointerGuard } from "./lib/overlayPointerGuard";
import { installOverlayBackButton } from "./lib/overlayBackButton";

// Watchdog global: impede que resíduo de modal (pointer-events/inert no <body>)
// deixe a tela travada com um overlay que "não fecha".
installOverlayPointerGuard();
// A-3: botão Voltar (inclusive o físico do Android) fecha o modal do topo.
installOverlayBackButton();



// Aplica overrides `?ff=` / `?ff_off=` antes de qualquer render — 100% browser-local.
applyFeatureFlagOverridesFromURL();

const isNativeCapacitor = () => {
  try {
    return !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } })
      .Capacitor?.isNativePlatform?.();
  } catch {
    return false;
  }
};

let nativeSplashHidePromise: Promise<void> | null = null;

const hideNativeSplashOnce = () => {
  try {
    if (!isNativeCapacitor()) return;
    if (nativeSplashHidePromise) return;
    nativeSplashHidePromise = (async () => {
      try {
        const { SplashScreen } = await import("@capacitor/splash-screen");
        await SplashScreen.hide().catch(() => undefined);
      } catch {
        /* no-op: web/preview */
      }
    })();
  } catch {
    /* no-op: web/preview */
  }
};

const container = document.getElementById("root")!;

/** Compara caminhos ignorando a barra final. */
const mesmoCaminho = (a: string, b: string) =>
  a.replace(/\/+$/, "").toLowerCase() === b.replace(/\/+$/, "").toLowerCase() ||
  (a === "/" && b.replace(/\/+$/, "") === "");

/**
 * Páginas públicas saem do build já com o HTML montado (SSG), para buscadores e
 * IAs lerem o texto sem executar JavaScript. No navegador esse HTML fica
 * visível enquanto o app carrega, e então o React assume a página.
 *
 * Não usamos `hydrateRoot` de propósito: as seções dessas páginas são carregadas
 * sob demanda (lazy), então o primeiro render no navegador nunca é idêntico ao
 * HTML do build — o React acusaria diferença no console e repintaria de todo
 * jeito. Sem hidratar, o resultado visual é o mesmo e o console fica limpo.
 *
 * A conferência do caminho é obrigatória: a hospedagem devolve o HTML da home
 * como fallback para rotas sem arquivo próprio (/auth, /entrada, app logado) —
 * nesse caso o HTML é descartado antes de aparecer.
 */
const rotaPreRenderizada = container.getAttribute(PRERENDER_ATTR);
const temHtmlDaRota =
  !!rotaPreRenderizada &&
  mesmoCaminho(rotaPreRenderizada, window.location.pathname) &&
  container.childElementCount > 0 &&
  !hasStoredAuthSession() &&
  !isNativeCapacitor();

let root: ReturnType<typeof createRoot> | null = null;

const ensureRoot = () => {
  if (!root) {
    container.innerHTML = "";
    root = createRoot(container);
  }
  return root;
};

if (!temHtmlDaRota) {
  // iPad/App Review: pinta algo leve imediatamente para o iOS liberar a
  // LaunchScreen nativa antes de baixar/avaliar o bundle principal do React.
  ensureRoot().render(
    <div className="min-h-[100dvh] bg-background flex items-center justify-center" aria-hidden="true">
      <div className="w-8 h-8 rounded-full border-2 border-brand/25 border-t-brand animate-spin" />
    </div>,
  );
}

window.setTimeout(hideNativeSplashOnce, 0);

/**
 * Monitoramento de erro e medição de terceiros entram DEPOIS da página estar
 * carregada (17/09/2026). Antes esperavam apenas a primeira ociosidade e, numa
 * home que leva segundos para pintar no celular, essa "ociosidade" acontecia
 * ANTES da imagem principal — os ~108 KB do monitoramento e o pixel disputavam
 * banda com a primeira tela. Nada foi removido: só passa para depois do `load`.
 *
 * Duas salvaguardas para não perder erro nenhum: um erro (ou promessa recusada)
 * antes do `load` liga o monitoramento na hora, e existe prazo máximo de 6 s
 * para o caso de a página nunca disparar `load` (mídia presa, rede ruim).
 */
const initObservability = () => {
  let iniciado = false;
  const run = () => {
    if (iniciado) return;
    iniciado = true;
    import("./lib/sentry").then(({ initSentry }) => initSentry()).catch(() => undefined);
    import("./lib/ga4").then(({ initGA4 }) => initGA4()).catch(() => undefined);
    import("./lib/metaPixelInit").then(({ initMetaPixel }) => initMetaPixel()).catch(() => undefined);
  };

  const requestIdle = (window as Window & {
    requestIdleCallback?: (cb: () => void, options?: { timeout?: number }) => number;
  }).requestIdleCallback;

  const depoisDoLoad = () => {
    if (typeof requestIdle === "function") requestIdle(run, { timeout: 2000 });
    else globalThis.setTimeout(run, 500);
  };

  // Erro antes do `load`: liga o monitoramento imediatamente.
  window.addEventListener("error", run, { once: true });
  window.addEventListener("unhandledrejection", run, { once: true });

  if (document.readyState === "complete") depoisDoLoad();
  else window.addEventListener("load", depoisDoLoad, { once: true });
  globalThis.setTimeout(run, 6000);
};

const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const isLovablePreview =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com");

if (isNativeCapacitor() && "serviceWorker" in navigator) {
  // Capacitor nativo: nunca registra SW. Limpa registros antigos se app foi convertido.
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => registration.unregister());
  });
} else if ((isInIframe || isLovablePreview) && "serviceWorker" in navigator) {
  // Preview/iframe: nunca registra SW (evita interferência no editor Lovable)
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => registration.unregister());
  });
} else if ("serviceWorker" in navigator) {
  // Produção: garante que app destrava de versão velha após deploy.
  // Quando SW novo assume controle (skipWaiting + clients.claim), recarrega 1x.
  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    refreshing = true;
    // Marca a causa do reload: sem isso, o PageView da carga seguinte podia
    // duplicar o da carga original (corrida medida em ~74ms na auditoria).
    try {
      sessionStorage.setItem("m360:sw_reload", String(Date.now()));
    } catch {
      /* ignora storage restrito */
    }
    window.location.reload();
  });


  // Produção: registra SW de push + HTML SWR cache (F5.1) — escopo raiz
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch((err) => console.warn("[sw] register failed", err));
  });
}

/** Mesma tela do .catch, extraída para o timeout poder reusá-la. */
const renderBootError = () => {
  ensureRoot().render(
    <div className="min-h-[100dvh] bg-background flex items-center justify-center px-6">
      <div className="max-w-[320px] text-center">
        <h1 className="font-display text-[22px] text-text-strong mb-2">Não foi possível abrir agora</h1>
        <p className="text-[14px] leading-6 text-text-muted mb-5">Tente fechar e abrir o app novamente.</p>
        <button className="cta-primary px-5 py-3" onClick={() => window.location.reload()}>
          Tentar de novo
        </button>
      </div>
    </div>,
  );
};

/**
 * Watchdog do boot (12/08/2026).
 *
 * O .catch abaixo cobre o import que REJEITA. Não cobre o que não resolve nem
 * rejeita — rede pendurada, chunk servido pela metade — e nesse caso a promise
 * fica pendente para sempre. Como o splash nativo já foi escondido no
 * setTimeout(0) acima, a mãe ficava olhando o spinner girar indefinidamente,
 * sem erro, sem botão e sem nada no log.
 *
 * Aos 10s troca pela mesma tela de erro, que tem recarregar.
 *
 * Se o import resolver DEPOIS do timeout, a tela de erro fica: `settled` evita
 * que o app apareça de repente sob o dedo de quem já está lendo o aviso. O
 * botão recarrega, e aí o chunk vem do cache.
 */
const BOOT_TIMEOUT_MS = 10_000;
let settled = false;
const bootTimeout = window.setTimeout(() => {
  if (settled) return;
  settled = true;
  console.error("[boot] timeout: App.tsx não resolveu em", BOOT_TIMEOUT_MS, "ms");
  hideNativeSplashOnce();
  renderBootError();
}, BOOT_TIMEOUT_MS);

/**
 * A /entrada abre por uma casca própria (`EntradaApp`), sem os contextos do app
 * logado. É a página de campanha paga: cada segundo de espera ali é dinheiro.
 * No app da loja (Capacitor) o arranque continua sendo o `App.tsx` completo.
 */
const rotaLeveDeCampanha =
  mesmoCaminho(window.location.pathname, "/entrada") && !isNativeCapacitor();

/**
 * As páginas públicas de conteúdo (home, /parceiras, /empresas, textos legais e
 * as /lp/) abrem pela casca `PublicoApp`, também sem os contextos do app logado.
 * Quem tem sessão guardada e o app da loja seguem pelo `App.tsx` completo.
 */
const CAMINHOS_PUBLICOS_LEVES = [
  "/",
  "/app",
  "/parceiras",
  "/empresas",
  "/termos",
  "/termos-uso",
  "/privacidade",
  "/politica-privacidade",
  "/politica-de-privacidade",
  "/politica-cookies",
];

const rotaPublicaLeve =
  !rotaLeveDeCampanha &&
  !isNativeCapacitor() &&
  !hasStoredAuthSession() &&
  (CAMINHOS_PUBLICOS_LEVES.some((p) => mesmoCaminho(window.location.pathname, p)) ||
    /^\/lp\/[^/]+\/?$/.test(window.location.pathname));

/** Monitoramento e Pixel esperam a ociosidade em toda casca leve. */
const rotaLeve = rotaLeveDeCampanha || rotaPublicaLeve;

/**
 * Os dois `import()` ficam em chamadas separadas de propósito. Num ternário
 * dentro da mesma expressão, o empacotador junta as duas listas de dependências
 * e o navegador baixa o app inteiro mesmo quando só a casca leve vai rodar
 * (medido: 1094 KB na /entrada). Medido depois de separar: ver comentário acima.
 */
const iniciar = (raiz: Promise<{ default: React.ComponentType }>) =>
  raiz
    .then(({ default: App }) => {
    window.clearTimeout(bootTimeout);
    if (settled) return;
    settled = true;
    // Abriu: a marca de "já recarreguei" sai, para uma falha futura nesta mesma
    // aba (outra publicação) poder recarregar de novo.
    try {
      sessionStorage.removeItem("m360:boot_reload");
    } catch {
      /* storage bloqueado: sem efeito */
    }
    ensureRoot().render(<App />);
    hideNativeSplashOnce();
    /**
     * Na /entrada o monitoramento (Sentry, ~106 KB) e o Pixel esperam a ociosidade:
     * é página de anúncio pago e eles competiam por banda com a primeira tela.
     * Nas outras rotas segue imediato, como antes.
     */
    if (rotaLeve && typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => initObservability(), { timeout: 4000 });
    } else {
      initObservability();
    }
    /**
     * `app_opened_preauth` também no web: `nativeBootstrap` só cobre o app da
     * loja, e o funil do site (a maior parte do tráfego pago) ficaria sem
     * denominador. Uma vez por aba, para não inflar a contagem por navegação.
     */
    try {
      if (!sessionStorage.getItem("m360:preauth_boot")) {
        sessionStorage.setItem("m360:preauth_boot", "1");
        void import("./lib/preauthEvents").then(({ trackPreauth }) =>
          trackPreauth("app_opened_preauth", { source: "web_boot" }),
        );
      }
    } catch {
      /* telemetria nunca quebra o boot */
    }
  })

    .catch((error) => {
      window.clearTimeout(bootTimeout);
      if (settled) return;
      settled = true;
      console.error("[boot] failed to load app", error);
      /**
       * Publicação nova = arquivos com nome novo (19/09/2026).
       *
       * Quem está com o HTML antigo — aba aberta há horas, cache do navegador,
       * cache do service worker — pede um arquivo que já não existe e o import
       * falha ("Failed to fetch dynamically imported module"). Não é defeito do
       * app: é o HTML velho apontando para o pacote velho. Recarregar uma vez
       * resolve, porque o HTML novo vem com os nomes novos.
       *
       * A marca em `sessionStorage` evita laço: se já recarregamos uma vez e
       * falhou de novo, aí sim mostramos a tela de erro com o botão.
       */
      /**
       * "Failed to fetch" também acontece SEM conexão — e aí o erro não é
       * pacote velho. Se o navegador se diz offline, não limpamos nada:
       * apagar service worker e caches tiraria da mãe justamente o que faz o
       * app abrir sem internet. Mostramos o aviso de conexão e o recarregar
       * fica por conta dela, quando a rede voltar.
       */
      if (navigator.onLine === false) {
        renderSemConexao();
        return;
      }
      if (ehFalhaDePacoteAntigo(error) && tentarRecarregarUmaVez()) {
        void recarregarLimpando();
        return;
      }
      renderBootError();
    });

/**
 * Falha de boot com o navegador offline: aviso próprio, SEM limpar caches.
 * O botão recarrega a página como está — com rede de volta, o import resolve.
 */
const renderSemConexao = () => {
  ensureRoot().render(
    <div className="min-h-[100dvh] bg-background flex items-center justify-center px-6">
      <div className="max-w-[320px] text-center">
        <h1 className="font-display text-[22px] text-text-strong mb-2">Você está sem conexão</h1>
        <p className="text-[14px] leading-6 text-text-muted mb-5">
          Confira sua internet e tente de novo.
        </p>
        <button className="cta-primary px-5 py-3" onClick={() => window.location.reload()}>
          Tentar de novo
        </button>
      </div>
    </div>,
  );
};

/** A falha é "arquivo com nome antigo", não erro dentro do app? */
function ehFalhaDePacoteAntigo(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null;
  const texto = `${e?.name ?? ""} ${e?.message ?? ""}`.toLowerCase();
  return (
    texto.includes("dynamically imported module") ||
    texto.includes("importing a module script failed") ||
    texto.includes("chunk") ||
    (texto.includes("failed to fetch") && texto.includes("module")) ||
    texto.includes("does not provide an export")
  );
}

const CHAVE_RECARGA_BOOT = "m360:boot_reload";

/** `true` na primeira falha da aba; `false` da segunda em diante. */
function tentarRecarregarUmaVez(): boolean {
  try {
    if (sessionStorage.getItem(CHAVE_RECARGA_BOOT)) return false;
    sessionStorage.setItem(CHAVE_RECARGA_BOOT, String(Date.now()));
    return true;
  } catch {
    // Sem sessionStorage não há como evitar laço: melhor mostrar o erro.
    return false;
  }
}

/**
 * Recarrega derrubando o que serviu o HTML antigo. Sem limpar o service worker
 * e os caches, o reload volta a receber o mesmo HTML velho e o erro repete.
 */
async function recarregarLimpando(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* recarrega de qualquer forma */
  }
  window.location.reload();
}

if (rotaLeveDeCampanha) {
  // O marcador não é decorativo: sem uma diferença entre os blocos, o
  // minificador reescreve o `if/else` como ternário e as listas de
  // dependências voltam a se juntar (o navegador baixaria o app inteiro).
  document.documentElement.dataset.raiz = "entrada";
  iniciar(import("./EntradaApp.tsx"));
} else if (rotaPublicaLeve) {
  document.documentElement.dataset.raiz = "publico";
  iniciar(import("./PublicoApp.tsx"));
} else {
  document.documentElement.dataset.raiz = "app";
  iniciar(import("./App.tsx"));
}
